import * as Sentry from '@sentry/react';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import dayjs, { Dayjs } from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { computeAvailability } from '../../../lib/computeAvailability.ts';
import { identify } from '../../../lib/analytics/index.ts';
import { IS_EMBED } from '../../../lib/isEmbed.ts';
import {
  getPaymentMethods,
  getReservationConfirmation,
  getRestaurant,
  getUser,
} from '../../../lib/api.ts';
import { ApiError } from '../../../lib/ApiError.ts';
import {
  ConfirmationResponseReservation,
  EventReservationData,
  ReservationResponseReservation,
  RestaurantResponseData,
  RestaurantResponseMeta,
  AuthResponse,
  Post_Reserve,
  PaymentMethod,
} from '../../../lib/types.ts';
import usePromise from 'react-promise-suspense';
import { UserDetails, WIDGET_STATE_VERSION, WidgetState } from './types.ts';

dayjs.extend(isToday);

// Synchronous storage backed by localStorage (shared across tabs) for auth
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const localSyncStorage = createJSONStorage<any>(() => localStorage);

// Synchronous storage backed by sessionStorage (per-tab) for booking state
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const sessionSyncStorage = createJSONStorage<any>(() => sessionStorage);

// In the embed, the flow runs on the RESTAURANT's origin — the member's auth
// token and booking state must never touch the host site's localStorage /
// sessionStorage. Back the atoms with an in-memory store instead. `resetEmbedStorage()`
// is called by the web-component on each open so a re-open (or a second widget
// instance) starts clean rather than inheriting the previous session.
const embedAuthMap = new Map<string, string>();
const embedSessionMap = new Map<string, string>();
const memoryStorageOver = (map: Map<string, string>): Storage =>
  ({
    getItem: (k: string) => (map.has(k) ? (map.get(k) as string) : null),
    setItem: (k: string, v: string) => void map.set(k, v),
    removeItem: (k: string) => void map.delete(k),
    clear: () => map.clear(),
    key: () => null,
    length: 0,
  }) as unknown as Storage;
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const embedAuthStorage = createJSONStorage<any>(() => memoryStorageOver(embedAuthMap));
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const embedSessionStorage = createJSONStorage<any>(() => memoryStorageOver(embedSessionMap));

export const resetEmbedStorage = () => {
  embedAuthMap.clear();
  embedSessionMap.clear();
};

// Storage key WidgetStateAtom serializes under; read directly (as JSON) by the
// reset helper so the decision stays a pure function of the module Maps.
const EMBED_SESSION_KEY = 'widget_reservation';
// Separate slot in the session Map holding the restaurant id of the current embed
// session, so a reopen can detect a cross-restaurant switch (rule (c) below).
const EMBED_RESTAURANT_KEY = 'widget_restaurant_id';

/** Read the persisted embed session blob (booking state) without touching the DOM. */
const getEmbedSessionState = (): Partial<WidgetState> | null => {
  const raw = embedSessionMap.get(EMBED_SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Partial<WidgetState>;
  } catch {
    return null;
  }
};

/** Restaurant id recorded for the current embed session (null on the first open). */
export const getEmbedSessionRestaurantId = (): string | null =>
  embedSessionMap.get(EMBED_RESTAURANT_KEY) ?? null;

/** Record the restaurant id for the current embed session (for cross-open compares). */
export const setEmbedSessionRestaurantId = (restaurantId: string): void => {
  embedSessionMap.set(EMBED_RESTAURANT_KEY, restaurantId);
};

/**
 * Decide whether an embed overlay OPEN should wipe the in-memory session
 * (auth token + booking state) or resume the previous one.
 *
 * Semantics:
 *  - Reopen within the TTL of the SAME restaurant => RESUME (returns false): a
 *    member who accidentally closes (backdrop tap / back-swipe) keeps their OTP
 *    auth and booking progress rather than being sent back through verification.
 *  - A full page reload => fresh session BY DESIGN: the module-scope Maps live in
 *    RAM only, so a reload drops everything. That is the deliberate privacy
 *    trade-off that keeps the member bearer token out of the partner origin.
 *  - Auth token + booking state are NEVER written to partner-origin storage
 *    (localStorage / sessionStorage / cookies) — they live only in the module Maps.
 *
 * Returns TRUE (caller should reset) when ANY of:
 *  (a) the session TTL (`WidgetState.expires`) has lapsed (`now > expires`);
 *  (b) the previous session ended in a COMPLETED booking (`completed` flag);
 *  (c) the restaurant id changed between opens (stale cross-restaurant state).
 * Otherwise FALSE (preserve/resume the session).
 *
 * Pure over the module Maps + args, so it is unit-testable without a DOM.
 */
export const shouldResetEmbedSession = (
  incomingRestaurantId: string,
  now: number = Date.now()
): boolean => {
  // (c) Restaurant switched between opens — never leak one venue's state to another.
  const storedRestaurantId = getEmbedSessionRestaurantId();
  if (storedRestaurantId !== null && storedRestaurantId !== incomingRestaurantId) {
    return true;
  }

  const session = getEmbedSessionState();
  if (!session) return false;

  // (b) Previous session already reached a confirmed booking — start clean.
  if (session.completed === true) return true;

  // (a) TTL lapsed since the last interaction (expires is stored as an ISO string).
  if (session.expires !== undefined) {
    const expiresMs = dayjs(session.expires as unknown as string).valueOf();
    if (Number.isFinite(expiresMs) && now > expiresMs) return true;
  }

  return false;
};

const getInitialValue = <T>(
  key: string,
  defaultValue: T,
  storageType: 'local' | 'session' = 'local'
): T => {
  if (typeof window === 'undefined') return defaultValue;
  try {
    const storage = storageType === 'session' ? window.sessionStorage : window.localStorage;
    const stored = storage.getItem(key);
    return stored ? (JSON.parse(stored) as T) : defaultValue;
  } catch {
    return defaultValue;
  }
};

export const AuthAtom = atomWithStorage<AuthResponse | null>(
  'widget_auth',
  IS_EMBED ? null : getInitialValue('widget_auth', null, 'local'),
  IS_EMBED ? embedAuthStorage : localSyncStorage,
  { getOnInit: true }
);
export const UserAtom = atom<UserDetails>({});
export const RestaurantIdAtom = atom<number | null>(null);
export const RestaurantAtom = atom<RestaurantResponseData | null>(null);
export const RestaurantMetaAtom = atom<RestaurantResponseMeta | null>(null);
export const RestaurantConfirmationAtom = atom<ConfirmationResponseReservation | null>(null);
export const ReservationAtom = atom<ReservationResponseReservation | null>(null);

export const EventReservationAtom = atom<EventReservationData | null>(null);
export const LoadPaymentMethodsAtom = atom<Dayjs>(dayjs());
export const PaymentMethodsAtom = atom<PaymentMethod[]>([]);
export const PaymentMethodsByIdAtom = atom<Record<string, PaymentMethod>>({});

const getExpiration = (): Dayjs => dayjs().add(30, 'minute');

// Fallback copy for booking failures the confirm hooks can't classify [HOS-2029]
const GENERIC_BOOKING_ERROR = 'Something went wrong completing your booking. Please try again.';

const defaultWidgetState: WidgetState = {
  version: WIDGET_STATE_VERSION,
  expires: getExpiration(),
  phone_country: 'US',
  phone_confirmed: null,
  policy_accepted: null,
  event: undefined,
  eventInfo: undefined,
  referral_code: undefined,
  alternative_pickup_first_name: undefined,
  alternative_pickup_last_name: undefined,
  alternative_pickup_phone: undefined,
  alternative_pickup_email: undefined,
  marketing_sms_opt_in: true,
};

export const WidgetStateAtom = atomWithStorage<WidgetState>(
  'widget_reservation',
  IS_EMBED
    ? defaultWidgetState
    : getInitialValue('widget_reservation', defaultWidgetState, 'session'),
  IS_EMBED ? embedSessionStorage : sessionSyncStorage,
  { getOnInit: true }
);

export const updateWidgetStateAtom = atom(null, (get, set, update: Partial<WidgetState>) => {
  const currentState = get(WidgetStateAtom);
  set(WidgetStateAtom, {
    ...currentState,
    ...update,
    expires: getExpiration(),
  });
});

/**
 * Single writer for the guest choosing the booking charge method (saved
 * card, new card, saved bank, or Plaid link). Selecting a method invalidates
 * any previous payment-failure banner, so the write and the clear stay
 * together [HOS-2029]. Auto-selection on page load must NOT go through here —
 * it would wipe the banner the guest was bounced back to read.
 */
export const widgetPaymentMethodSelection = (
  paymentMethodId: string,
  extras: Partial<WidgetState> = {}
): Partial<WidgetState> => ({
  ...extras,
  payment_method: paymentMethodId,
  payment_error: undefined,
});

/**
 * Companion writer for removing the selected charge method ("Change Card",
 * flow resets). Dropping the method also drops a failure banner about it —
 * the guest is already acting on the failure.
 */
export const widgetPaymentMethodDeselection = (): Partial<WidgetState> => ({
  payment_method: undefined,
  payment_error: undefined,
});

/**
 * Decides whether this widget booker's `POST /v2.0/verification` should carry
 * `subscribe_to_general_marketing_sms: true` [GRO-7075].
 *
 * The backend's `GeneralMarketingSms` subscription group excludes GB and only
 * has US Braze IDs wired, and the Member app's `PhoneRegistration` pre-selects
 * marketing SMS only when `countryCode === 'US'`. We mirror that gate so that
 * a non-US widget booker is never enrolled in the wrong Braze group, and (as a
 * defence in depth) so DetailsPage and VerifyPage cannot drift apart on where
 * the country check happens.
 *
 * Opt-in only: an unchecked box or a non-US country returns `false`, which the
 * `verification()` client then translates to omitting the wire key entirely
 * (never sending `false`), matching the "never unsubscribe" invariant.
 */
export const resolveWidgetMarketingSmsOptIn = (widget: WidgetState): boolean => {
  const isUsPhone = (widget.phone_country ?? 'US') === 'US';
  const optedIn = widget.marketing_sms_opt_in ?? true;

  return isUsPhone && optedIn;
};

/**
 * Friendlier copy for the one `updateUser` rejection whose raw API message
 * describes the database constraint rather than the guest's situation.
 */
const EMAIL_TAKEN_API_MESSAGE = 'The email has already been taken.';
const EMAIL_TAKEN_GUEST_MESSAGE =
  'The given email address is associated with a Dorsia Membership with a different phone number.';

/**
 * Same treatment for the email-domain rejection, whose API copy ends in "check
 * the spelling" — advice that made sense before we could name the domain and now
 * asks the guest to re-do work we already did. The replacement matches the member
 * app and dorsia-web word for word, so one rejection reads the same everywhere.
 */
const EMAIL_DOMAIN_API_MESSAGE =
  'This email domain cannot receive mail. Please check the spelling.';
const emailDomainGuestMessage = (typedEmail: string | undefined): string => {
  const domain = typedEmail?.split('@')[1];

  // No domain to name (an address the API rejected before we stored it) leaves
  // the API's sentence in place rather than printing a half-built one.
  return domain ? `Looks like a typo, @${domain} can't receive mail` : EMAIL_DOMAIN_API_MESSAGE;
};

/**
 * Single writer for an `updateUser` rejection raised on `../verify`, to be
 * rendered on `../details` [GRO-6429].
 *
 * The guest's email is only editable on the details page, but `updateUser` does
 * not run until after the OTP on the verify page — so the rejection has to
 * travel backwards across that navigation. `update_user_error` already did;
 * `email_domain_suggestion` now rides with it rather than through a second,
 * parallel mechanism.
 *
 * All three keys are ALWAYS written, never conditionally. A suggestion is
 * attached only when the API found one, so a second rejection without one (a
 * different email problem, or the same domain after the suggestion list changed)
 * must clear the first — otherwise the details page keeps offering a correction
 * for an address the guest has already moved on from.
 *
 * The message lands in `email_error`, NOT `update_user_error`. Every rejection
 * `ApiError` types as `UpdateUserErrorResponse` reads its message from
 * `errors.email[0]`, so this is always a problem with one field — and it renders
 * in that field's slot. `update_user_error` stays for genuinely form-level
 * failures (the send-code error), which belong at the top of the form. Sharing
 * one key made the email sentence render above `First name`, describing a field
 * three rows below it; splitting them puts each message where its cause is
 * instead of choosing a position with a conditional.
 */
export const widgetUpdateUserRejection = (
  error: ApiError,
  typedEmail?: string
): Partial<WidgetState> => ({
  email_error:
    error.message === EMAIL_TAKEN_API_MESSAGE
      ? EMAIL_TAKEN_GUEST_MESSAGE
      : error.message === EMAIL_DOMAIN_API_MESSAGE
        ? emailDomainGuestMessage(typedEmail)
        : error.message,
  // A field-level rejection clears any stale form-level error, so the guest is
  // never told two different things about one submission.
  update_user_error: undefined,
  // `ApiError` reports "no suggestion" as null; WidgetState is a stored,
  // JSON-serialized shape whose absent keys are undefined.
  email_domain_suggestion: error.emailDomainSuggestion ?? undefined,
});

export const resetWidgetStateAtom = atom(null, (_get, set) => {
  set(WidgetStateAtom, {
    version: WIDGET_STATE_VERSION,
    expires: getExpiration(),
    phone_country: 'US',
    phone_confirmed: null,
    policy_accepted: null,
    event: undefined,
    eventInfo: undefined,
    referral_code: undefined,
    alternative_pickup_first_name: undefined,
    alternative_pickup_last_name: undefined,
    alternative_pickup_phone: undefined,
    alternative_pickup_email: undefined,
    marketing_sms_opt_in: true,
  });
});

export const reloadPaymentMethodsAtom = atom(null, (_get, set) => {
  set(LoadPaymentMethodsAtom, dayjs());
});

export const useWidgetState = (): [WidgetState, (partial: Partial<WidgetState>) => void] => {
  const widget = useAtomValue(WidgetStateAtom);
  const setWidgetState = useSetAtom(updateWidgetStateAtom);

  const update = (partial: Partial<WidgetState>) => {
    setWidgetState(partial);
  };

  return [widget, update];
};

export const useRestaurantId = () => {
  const { id } = useParams();
  const [restaurant_id, setRestaurantId] = useAtom(RestaurantIdAtom);

  // Parse ID synchronously to avoid null on first render
  const parsed_id = id ? parseInt(id, 10) : null;

  useEffect(() => {
    if (parsed_id !== null && parsed_id !== restaurant_id) {
      setRestaurantId(parsed_id);
    }
  }, [parsed_id, restaurant_id, setRestaurantId]);

  // Return the parsed ID immediately, don't wait for atom update
  return parsed_id ?? restaurant_id;
};

// Updating this key will force an API hit to load the latest restaurant availability
let RELOAD_RESTAURANT_KEY = dayjs().format();

export const reloadRestaurantKey = () => {
  RELOAD_RESTAURANT_KEY = dayjs().format();
};

export const useResetState = (): (() => void) => {
  const resetReservation = useSetAtom(resetWidgetStateAtom);

  return () => {
    reloadRestaurantKey();
    resetReservation();
  };
};

export const useRestaurant = () => {
  const NOW = dayjs();
  const id = useRestaurantId();
  const auth = useAtomValue(AuthAtom);
  const [widget] = useWidgetState();
  const [storedRestaurant, setRestaurant] = useAtom(RestaurantAtom);
  const [storedMeta, setMeta] = useAtom(RestaurantMetaAtom);

  let og_date = NOW.format('YYYY-MM-DD');
  let date = dayjs();
  let time = '0:00';

  if (widget.date) {
    og_date = widget.date;
    date = dayjs(og_date);
  }

  if (date.isToday()) {
    time = NOW.format('H:mm');
  } else if (
    storedRestaurant?.operating_hours !== undefined &&
    storedRestaurant.operating_hours.length > 0
  ) {
    const day = date.day();

    if (storedRestaurant.operating_hours[day]?.length) {
      time = storedRestaurant.operating_hours[day][0].start;
    }
  }

  const response = usePromise(getRestaurant, [
    id,
    og_date,
    time,
    RELOAD_RESTAURANT_KEY,
    auth?.token ?? null,
  ]);

  // Compute meta synchronously from response to avoid re-render
  const computedMeta = response?.meta?.availability
    ? computeAvailability(response.meta)
    : (response?.meta ?? null);

  // Update atoms in background for other components, but don't wait for it
  useEffect(() => {
    if (response?.data && response.data.id !== storedRestaurant?.id) {
      setRestaurant(response.data);
    }

    if (response?.meta) {
      const r_length = response.meta.availability?.length || 0;
      const m_length = storedMeta?.availability?.length ?? 0;
      if (
        response.meta?.date !== storedMeta?.date ||
        response.meta?.availability[0]?.uid !== storedMeta?.availability[0]?.uid ||
        r_length !== m_length
      ) {
        setMeta(computedMeta);
      }
    }
  }, [response?.meta?.date, response?.data?.id]);

  // Return response data directly to avoid waiting for atom update
  return {
    restaurant: response?.data ?? storedRestaurant,
    meta: computedMeta ?? storedMeta,
  };
};

export const useConfirmEvent = () => {
  const id = useRestaurantId();
  const auth = useAtomValue(AuthAtom);
  const navigate = useNavigate();
  const [widget, updateWidget] = useWidgetState();
  const [confirmation, setConfirmation] = useAtom(RestaurantConfirmationAtom);

  useEffect(() => {
    const get = async () => {
      if (id && auth && widget?.confirm_body) {
        try {
          const response = await getReservationConfirmation(auth.token, id, widget.confirm_body);
          setConfirmation(response.reservation);

          const reserve: Post_Reserve = {
            restaurant: response.reservation.restaurant.id,
            seating: response.reservation.seating.id,
            seats: response.reservation.party,
            date: response.reservation.date_full,
            confirmed_price: response.reservation.price_per_person,
          };

          if (widget.payment_method) {
            reserve.payment_method_id = widget.payment_method;
          }

          updateWidget({
            reserve_body: reserve,
          });
        } catch (e: unknown) {
          if (
            e instanceof ApiError &&
            (e.type === 'ReserveErrorResponse' || e.type === 'CardErrorResponse')
          ) {
            updateWidget({
              payment_error: e.message,
            });

            navigate(`../payment`);
          } else if (
            e instanceof ApiError &&
            (e.type === 'ReservationUnavailableErrorResponse' ||
              e.type === 'ReservationResourceNotFound' ||
              e.type === 'AlreadyCanceledReservationErrorResponse')
          ) {
            reloadRestaurantKey();

            updateWidget({
              availability: undefined,
              option: undefined,
              reservation_error: e.message,
            });

            // navigate(`../booking`);
          } else if (e instanceof Error) {
            console.error('Unrecognized Error', e.message);
            Sentry.captureException(e);
            // Land the guest on the payment page WITH an explanation — a bare
            // redirect after an unclassified failure showed nothing [HOS-2029]
            updateWidget({ payment_error: GENERIC_BOOKING_ERROR });
            navigate(`../payment`);
          }
        }
      }
    };

    void get();
  }, [
    id,
    auth?.token,
    widget?.confirm_body?.restaurant_id,
    widget?.confirm_body?.date,
    widget?.confirm_body?.time,
    widget?.confirm_body?.party,
    widget?.confirm_body?.seating_id,
  ]);

  return confirmation;
};

export const useConfirmRestaurant = () => {
  const id = useRestaurantId();
  const auth = useAtomValue(AuthAtom);
  const navigate = useNavigate();
  const [widget, updateWidget] = useWidgetState();
  const [confirmation, setConfirmation] = useAtom(RestaurantConfirmationAtom);

  useEffect(() => {
    const get = async () => {
      if (id && auth && widget?.confirm_body) {
        try {
          const response = await getReservationConfirmation(auth.token, id, widget.confirm_body);
          setConfirmation(response.reservation);

          const reserve: Post_Reserve = {
            restaurant: response.reservation.restaurant.id,
            seating: response.reservation.seating.id,
            seats: response.reservation.party,
            date: response.reservation.date_full,
            confirmed_price: response.reservation.price_per_person,
            skip_level_check: true,
            use_credits: !!widget.confirm_body.use_credits,
          };

          if (widget.payment_method) {
            reserve.payment_method_id = widget.payment_method;
          }

          updateWidget({
            reserve_body: reserve,
          });
        } catch (e: unknown) {
          if (
            e instanceof ApiError &&
            (e.type === 'ReserveErrorResponse' || e.type === 'CardErrorResponse')
          ) {
            updateWidget({
              payment_error: e.message,
            });

            navigate(`../payment`);
          } else if (
            e instanceof ApiError &&
            (e.type === 'ReservationUnavailableErrorResponse' ||
              e.type === 'ReservationResourceNotFound')
          ) {
            reloadRestaurantKey();

            updateWidget({
              availability: undefined,
              option: undefined,
              reservation_error: e.message,
            });

            // navigate(`../booking`);
          } else if (e instanceof Error) {
            console.error('Unrecognized Error', e.message);
            Sentry.captureException(e);
            // Land the guest on the payment page WITH an explanation — a bare
            // redirect after an unclassified failure showed nothing [HOS-2029]
            updateWidget({ payment_error: GENERIC_BOOKING_ERROR });
            navigate(`../payment`);
          }
        }
      }
    };

    void get();
  }, [
    id,
    auth?.token,
    widget?.confirm_body?.restaurant_id,
    widget?.confirm_body?.date,
    widget?.confirm_body?.time,
    widget?.confirm_body?.party,
    widget?.confirm_body?.seating_id,
    widget?.confirm_body?.use_credits,
  ]);

  return confirmation;
};

export const useUserDetails = () => {
  const auth = useAtomValue(AuthAtom);
  const [user, setUser] = useAtom(UserAtom);
  const widget = useAtomValue(WidgetStateAtom);
  const setWidget = useSetAtom(updateWidgetStateAtom);

  useEffect(() => {
    const go = async () => {
      const response = await getUser(auth!.token);

      if (!response?.user) {
        return;
      }

      Sentry.setUser({
        id: response.user.uuid,
        email: response.user.email,
      });

      const traits = {
        first_name: response.user.first_name,
        last_name: response.user.last_name,
        email: response.user.email,
        phone: response.user.phone,
      };

      // Enrich the member's identity with profile traits. The id-only identify
      // happens earlier at the widget shell (Widget.tsx) so the top-of-funnel
      // events are already attributed; here we attach the server-validated
      // traits once they're loaded. Same member uuid throughout, so the repeat
      // identify is idempotent.
      identify(response.user.uuid, traits);

      setUser(traits);

      setWidget({
        first_name: response.user.first_name,
        last_name: response.user.last_name,
        email: response.user.email,
        phone_number: response.user.phone,
        phone_country: widget.phone_country ?? 'US',
      });
    };

    if (auth?.token) {
      void go();
    }
  }, [auth?.token]);

  return user;
};

export const usePaymentMethods = () => {
  const auth = useAtomValue(AuthAtom);
  const reload = useAtomValue(LoadPaymentMethodsAtom);
  const setMethods = useSetAtom(PaymentMethodsAtom);
  const setMethodsById = useSetAtom(PaymentMethodsByIdAtom);

  useEffect(() => {
    if (!auth?.token) {
      return;
    }

    const go = async () => {
      try {
        const response = await getPaymentMethods(auth.token);

        if (!response?.data) {
          setMethods([]);
          setMethodsById({});
          return;
        }

        setMethods(response.data);

        const by_id = {};
        response.data.map((method) => (by_id[method.id] = method));
        setMethodsById(by_id);
      } catch (e: unknown) {
        Sentry.captureException(e);
      }
    };

    void go();
  }, [auth?.token, reload]);
};
